import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import {
	BOTTOM_BAR_IDS,
	DEFAULT_UNCOLORED_COLOR,
	MESHES_IDS,
	SPLINE_MESH,
	STORAGE_NAMES,
	TOP_STYLE_PELMET,
	VIEW_TYPES,
} from '@root/app.config';
import { VariantService } from '@core/services/variant/variant.service';
import { EngineService } from '@core/services/engine/engine.service';
import { BlindStateService } from '@core/services/blind-state/blind-state.service';
import { SaveSessionBlindsService } from '@core/services/save-session-blinds/save-session-blinds.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import * as _ from 'lodash';
import { BlindData, BottomBar, Frames, IBlindSelect, MaterialType, SectionKeysEnum } from '@root/app.interfaces';
import { Subscription } from 'rxjs';
import { FrameColorComponent } from '@features/frame-color/frame-color.component';

@Component({
	selector: 'app-bottom-bar',
	templateUrl: './bottom-bar.component.html',
	styleUrls: ['./bottom-bar.component.scss', './bottom-bar.responsive.scss'],
})
export class BottomBarComponent implements OnInit, OnChanges, OnDestroy {
	@ViewChild('frameBottomBarItem') frameBottomBarItem: ElementRef<HTMLElement>;
	@Output() frameData: EventEmitter<{ frames: Frames }> = new EventEmitter();
	@Input() blindId: number;

	sessionText = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_text);
	sessionConfig = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config);
	blindType: 'interior' | 'outdoor' = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_type);
	isDesignType: boolean = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type) === VIEW_TYPES.design;

	defaultMeshes = TOP_STYLE_PELMET;
	defaultBottomBar = MESHES_IDS[22];
	framesData: { frames: Frames };
	bottomBar: BottomBar[] = [];
	customBottomBarId = 4;
	frameBottomBarObject = {
		id: 0,
		name: '',
		item: '',
		meshes: [],
	};

	isShop = this.variant.isShop();
	dontSetBottomBar = this.variant.isShop();

	blindSelectSubscription: Subscription;
	materialChangeSubscription: Subscription;

	constructor(
		private readonly variant: VariantService,
		private readonly engineService: EngineService,
		private readonly blindStateService: BlindStateService,
		private readonly saveSessionBlind: SaveSessionBlindsService,
		private readonly sessionStorageService: SessionStorageService,
		private readonly changeDetection: ChangeDetectorRef,
		private readonly shareDataService: ShareService,
	) {}

	ngOnInit() {
		this.getFrameFromStorage();

		if (this.blindType === 'outdoor') {
			this.bottomBar = this.sessionConfig?.[this.blindType].frame.bottom_bar_protection;
		}

		this.blindSelectSubscription = this.shareDataService.getBlindSelect.subscribe(this.setBlindSelectId.bind(this));
		this.materialChangeSubscription = this.shareDataService.getMaterialColorChanged.subscribe(this.onMaterialTypeChange.bind(this));
	}

	ngOnChanges(changes: SimpleChanges): void {
		const getCurrentBLindId = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_current_blind_id);

		if (changes.blindId.currentValue === getCurrentBLindId) {
			setTimeout(this.setDefaultValue.bind(this));
		}
	}

	ngOnDestroy() {
		this.blindSelectSubscription.unsubscribe();
		this.materialChangeSubscription.unsubscribe();
	}

	setDefaultValue(): void {
		const getBlindType = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_type);
		const frameStorage = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0];
		const frameConfig = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config)[getBlindType].frame;

		if (_.isEmpty(this.framesData?.frames)) {
			this.framesData = {
				frames: {
					frame_color: {
						id: null,
						name: null,
						color: null,
						color_name: null,
					},
					top_style: {
						id: null,
						name: null,
					},
					bottom_bar: {
						id: null,
						name: null,
					},
					optionals: [],
				},
			};
		}

		if (_.isEmpty(frameStorage && frameStorage.setup.frames?.bottom_bar) && frameConfig.bottom_bar_protection) {
			for (const bottomBar of frameConfig.bottom_bar_protection) {
				if (bottomBar.is_default) {
					this.defaultBottomBar = bottomBar.name;
					this.onSelectBottomBar(bottomBar, null, false);
				}
			}
		}

		if (!this.isDesignType && !_.isEmpty(frameStorage)) {
			this.setBlindSelectId({ id: this.blindId });
		}
	}

	onSelectBottomBar(data: BottomBar, event: Event, saveChangeTime = true, buttonPress = false): void {
		if (buttonPress) {
			this.dontSetBottomBar = false;
		}

		if (event) {
			FrameColorComponent.activeClassHandler(this.frameBottomBarItem.nativeElement, event.currentTarget as HTMLElement);
		}

		const bottomBarMeshes = BOTTOM_BAR_IDS.filter((item) => item.id === data.id)[0].meshes;

		const bottomBarData = {
			bottom_bar: {
				id: data.id,
				name: data.name,
				configured: !this.dontSetBottomBar,
				is_extra_fee: data.is_extra_fee,
			},
		};
		this.engineService.setBottomBar({
			meshes: bottomBarMeshes,
			type: event?.type,
			description: data.description,
		});

		this.framesData = { frames: { ...this.framesData.frames, ...bottomBarData } };
		this.saveBlindSetup(this.framesData, saveChangeTime);
	}

	setBlindSelectId(data: IBlindSelect): void {
		this.getFrameFromStorage(data.id);
		this.blindType = this.sessionStorageService.getBlindItemById(data?.id, STORAGE_NAMES.zip_blind_data)[0].type;

		if (data.status === 'new') {
			if (this.blindType === 'outdoor') {
				this.frameBottomBarObject.item = this.defaultBottomBar;
			}
		}

		if (this.blindType === 'outdoor') {
			const bottomBarData = BOTTOM_BAR_IDS.filter((item) => item.id === this.frameBottomBarObject?.id);

			this.bottomBar = this.sessionConfig[this.blindType].frame.bottom_bar_protection;
			this.frameBottomBarObject.meshes = bottomBarData[0]?.meshes || [this.defaultBottomBar];
		}

		if (data.id === this.blindId) {
			if (this.blindType === 'outdoor') {
				const frameBottomBar =
					_.isEmpty(this.frameBottomBarObject) && _.isEmpty(this.frameBottomBarObject.meshes)
						? this.defaultMeshes
						: this.frameBottomBarObject.meshes;

				this.engineService.setBottomBar({
					meshes: frameBottomBar,
					type: 'storage',
				});
			}
		}

		this.changeDetection.markForCheck();
	}

	getFrameFromStorage(id?: number | string): void {
		const blindType = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_type);
		const blinds = this.sessionStorageService.getBlindItemById(id || this.blindId, STORAGE_NAMES.zip_blind_data);

		if (blinds) {
			for (const blind of blinds) {
				if (blind.setup && blind.setup.frames && id === this.blindId) {
					const { bottom_bar } = blind.setup.frames;
					this.dontSetBottomBar = bottom_bar ? !bottom_bar.configured : this.dontSetBottomBar;

					this.framesData = { frames: { ...blind.setup.frames } };

					if (blindType === 'outdoor') {
						this.frameBottomBarObject = bottom_bar.configured ? bottom_bar : { ...this.frameBottomBarObject, name: ' ' };
					}
				}
			}
		}
	}

	saveBlindSetup(framesData = this.framesData, saveChangeTime = true): void {
		const blind: BlindData = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0];
		const { bottom_bar } = framesData.frames;
		framesData.frames = {
			...blind.setup.frames,
			bottom_bar,
		};
		this.frameData.emit(framesData);
		this.sessionStorageService.setBlindSetupDataById(this.blindId, framesData, STORAGE_NAMES.zip_blind_data, saveChangeTime);
		this.saveSessionBlind.PutStorageDataToServer();

		if (!this.dontSetBottomBar) {
			this.blindStateService.markConfigured(this.blindId, SectionKeysEnum.BottomBar);
		}
	}

	onMaterialTypeChange<T extends { type: string; blind_id: number; color: string }>(colorProperty: T): void {
		const isCurrentBlind = colorProperty.blind_id === this.blindId;
		const meshes = [...BOTTOM_BAR_IDS.filter((item) => item.id === this.customBottomBarId)[0].meshes];

		if (colorProperty.type === MaterialType.Sunscreen && isCurrentBlind) {
			meshes.push(...SPLINE_MESH);
			this.setBottomBarColor(colorProperty.color, meshes);
		}

		if (colorProperty.type === MaterialType.ClearPVC && isCurrentBlind) {
			this.setBottomBarColor(DEFAULT_UNCOLORED_COLOR, meshes);
		}
	}

	setBottomBarColor(color: string, meshes: string[]): void {
		for (const meshId of meshes) {
			this.engineService.setColor(meshId, color, 1, 'frame', this.blindType);
		}
	}
}
